import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { subAffDealsV2Service } from "../../../services/admin/subAffDealsV2.service";
import { OrderType } from "../../../components/affiliate/Trackers";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";

export type payloadSubAffDealsV2List = {
  page: number;
  perPage?: number;
  order: OrderType;
  filter: {
    search: string | null;
  };
};

export type listItem = {
  id: number;
  title: string;
  description: string;
  read_only: boolean;
  created_at: string;
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export interface ISubAffDealsV2ListData {
  list: listItem[];
	columns: columnsItem[];
	startRowNum: number;
	endRowNum: number;
	totalEntries: number | string;
	totalPages: number;
};


export type subAffDealsV2SliceState = {
  list: {
    data: ISubAffDealsV2ListData | null,
    error: any,
    isFetching: boolean,
  },
  deleteCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  constants: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  createCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  viewCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  editCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
	filterCreator: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: subAffDealsV2SliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  deleteCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
  constants: {
    data: null,
    error: null,
    isFetching: false,
  },
  createCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
  viewCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
  editCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
	filterCreator: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'subAffDealsV2/getListData',
  async (payload: payloadSubAffDealsV2List, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const deleteConditionData: any = createAsyncThunk(
  'subAffDealsV2/deleteCondition',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.deleteCondition(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok && response.status === 200) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.removed',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getConstantsData: any = createAsyncThunk(
  'subAffDealsV2/getConstantsData',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.getConstants();
      const data = await response.json();
      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const createConditionData: any = createAsyncThunk(
  'subAffDealsV2/createConditionData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.createCondition(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getViewConditionData: any = createAsyncThunk(
  'subAffDealsV2/getViewConditionData',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getCondition(id);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue({
          status: response.status,
          statusText: response.statusText,
          data: data,
        })
      }
      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const editConditionData: any = createAsyncThunk(
  'subAffDealsV2/editConditionData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.editCondition(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getFilterCreatorData: any = createAsyncThunk(
	'subAffDealsV2/getFilterCreatorData',
	async (payload: any, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await subAffDealsV2Service.getFilterCreator(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const subAffDealsV2Slice = createSlice({
  name: 'subAffDealsV2',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearDeleteConditionState(state) {
      state.deleteCondition.error = null;
      state.deleteCondition.isFetching = false;
      state.deleteCondition.data = null;
    },
    clearCreateConditionState(state) {
      state.createCondition.error = null;
      state.createCondition.isFetching = false;
      state.createCondition.data = null;
    },
    clearViewConditionState(state) {
      state.viewCondition.error = null;
      state.viewCondition.isFetching = false;
      state.viewCondition.data = null;
    },
    clearConstantsState(state) {
      state.constants.error = null;
      state.constants.isFetching = false;
      state.constants.data = null;
    },
    clearEditConditionState(state) {
      state.editCondition.error = null;
      state.editCondition.isFetching = false;
      state.editCondition.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<subAffDealsV2SliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

    builder.addCase(deleteConditionData.pending, (state ) => {
      state.deleteCondition.isFetching = true;
      state.deleteCondition.error = null;
    });
    builder.addCase(deleteConditionData.fulfilled, (state , action) => {
      state.deleteCondition.error = null;
      state.deleteCondition.isFetching = false;
      state.deleteCondition.data = action.payload;
    });
    builder.addCase(deleteConditionData.rejected, (state , action) => {
      state.deleteCondition.isFetching = false;
      state.deleteCondition.error = action.payload;
      state.deleteCondition.data = null;
    });

    builder.addCase(getConstantsData.pending, (state ) => {
      state.constants.isFetching = true;
      state.constants.error = null;
    });
    builder.addCase(getConstantsData.fulfilled, (state , action) => {
      state.constants.error = null;
      state.constants.isFetching = false;
      state.constants.data = action.payload;
    });
    builder.addCase(getConstantsData.rejected, (state , action) => {
      state.constants.isFetching = false;
      state.constants.error = action.payload;
      state.constants.data = null;
    });

    builder.addCase(createConditionData.pending, (state ) => {
      state.createCondition.isFetching = true;
      state.createCondition.error = null;
    });
    builder.addCase(createConditionData.fulfilled, (state , action) => {
      state.createCondition.error = null;
      state.createCondition.isFetching = false;
      state.createCondition.data = action.payload;
    });
    builder.addCase(createConditionData.rejected, (state , action) => {
      state.createCondition.isFetching = false;
      state.createCondition.error = action.payload;
      state.createCondition.data = null;
    });

    builder.addCase(getViewConditionData.pending, (state ) => {
      state.viewCondition.isFetching = true;
      state.viewCondition.error = null;
    });
    builder.addCase(getViewConditionData.fulfilled, (state , action) => {
      state.viewCondition.error = null;
      state.viewCondition.isFetching = false;
      state.viewCondition.data = action.payload;
    });
    builder.addCase(getViewConditionData.rejected, (state , action) => {
      state.viewCondition.isFetching = false;
      state.viewCondition.error = action.payload;
      state.viewCondition.data = null;
    });

    builder.addCase(editConditionData.pending, (state ) => {
      state.editCondition.isFetching = true;
      state.editCondition.error = null;
    });
    builder.addCase(editConditionData.fulfilled, (state , action) => {
      state.editCondition.error = null;
      state.editCondition.isFetching = false;
      state.editCondition.data = action.payload;
    });
    builder.addCase(editConditionData.rejected, (state , action) => {
      state.editCondition.isFetching = false;
      state.editCondition.error = action.payload;
      state.editCondition.data = null;
    });

		builder.addCase(getFilterCreatorData.pending, (state ) => {
      state.filterCreator.isFetching = true;
      state.filterCreator.error = null;
    });
    builder.addCase(getFilterCreatorData.fulfilled, (state , action) => {
      state.filterCreator.error = null;
      state.filterCreator.isFetching = false;
      state.filterCreator.data = action.payload;
    });
    builder.addCase(getFilterCreatorData.rejected, (state , action) => {
      state.filterCreator.isFetching = false;
      state.filterCreator.error = action.payload;
      state.filterCreator.data = null;
    });
  },
});

export const {
  clearListState,
  clearDeleteConditionState,
  clearCreateConditionState,
  clearViewConditionState,
  clearConstantsState,
  clearEditConditionState,
} = subAffDealsV2Slice.actions;

export default subAffDealsV2Slice.reducer;
